class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  connectedCallback() {
    // Prefetch variant data on hover/focus/change
    const optionInputs = this.querySelectorAll('fieldset input, select');
    optionInputs.forEach(input => {
      ['mouseenter', 'focus', 'change'].forEach(eventType => {
        input.addEventListener(eventType, e => {
          this.prefetchVariant(e);
        });
      });
    });
  }

  prefetchVariant(e) {
    // Compute intended variant/options
    let selectedOptions = [];
    if (e.target.tagName === 'SELECT') {
      selectedOptions = Array.from(this.querySelectorAll('select')).map(sel => sel.value);
    } else {
      // For radios, get checked in each fieldset
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      selectedOptions = fieldsets.map(fieldset => {
        const checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    // Build params for product section fetch
    const productSectionId = this.dataset.section;
    let params = `section_id=${productSectionId}&option_values=${selectedOptions.join(',')}`;
    const newProductUrl = this.dataset.url || window.location.pathname;
    const sectionCacheKey = `${newProductUrl}?${params}`;
    const sectionCache = VariantSelects.productSectionCache;
    if (!sectionCache.has(sectionCacheKey)) {
      fetch(sectionCacheKey)
        .then(r => r.text())
        .then(text => sectionCache.set(sectionCacheKey, text));
    }

    // Build params for variant info fetch
    // Try to find the variant ID for these options
    let variantId = null;
    try {
      const variants = this.getVariantData();
      const match = variants.find(variant => {
        return variant.options.every((opt, idx) => selectedOptions[idx] === opt);
      });
      if (match) variantId = match.id;
    } catch (err) {}
    if (variantId) {
      const infoCacheKey = `${newProductUrl}?variant=${variantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`;
      const infoCache = VariantSelects.variantInfoCache;
      if (!infoCache.has(infoCacheKey)) {
        fetch(infoCacheKey)
          .then(r => r.text())
          .then(text => infoCache.set(infoCacheKey, text));
      }
    }
  }

  onVariantChange(e) {
    const { isCombinedProduct, targetUrl } = this.combinedListingCheck(e);

    this.toggleAddButton(true, '', false);

    if (isCombinedProduct) {
      this.fetchProductSection(targetUrl);
      return; // Stop execution if false
    }

    this.updateOptions();
    this.updateMasterId();
    this.updatePickupAvailability();
    this.updateInventoryStatus();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  combinedListingCheck(e) {
    const currentUrl = window.location.pathname;
    const targetUrl = e.target.dataset.productUrl;
    let isCombinedProduct = false;

    if (targetUrl != undefined && currentUrl != targetUrl) {
      isCombinedProduct = true;
    }

    return { isCombinedProduct, targetUrl };
  }

  // Static cache for fetch responses
  static productSectionCache = new Map();
  async fetchProductSection(newProductUrl) {
    const productElement = this.closest('.main-product');
    const productSectionId = this.dataset.section;
    let params = `section_id=${productSectionId}&`;

    const selectedOptionValues = Array.from(this.querySelectorAll('fieldset input:checked'))
      .map(element => element.dataset.optionValueId)
      .join(',');
    params += `option_values=${selectedOptionValues}`;

    const cacheKey = `${newProductUrl}?${params}`;
    const cache = VariantSelects.productSectionCache;

    if (cache.has(cacheKey)) {
      const responseText = cache.get(cacheKey);
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      productElement.innerHTML = html.querySelector('.main-product').innerHTML;
      window.history.replaceState({}, '', `${newProductUrl}`);
      return;
    }

    fetch(cacheKey)
      .then(response => response.text())
      .then(responseText => {
        cache.set(cacheKey, responseText);
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        productElement.innerHTML = html.querySelector('.main-product').innerHTML;
        window.history.replaceState({}, '', `${newProductUrl}`);
      });
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), select => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find(variant => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });

    // Always synchronize variant ID with global tracker on any variant change
    if (this.currentVariant && this.currentVariant.id) {
      window.latestVariantId = this.currentVariant.id;
    }
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGridySlider = document.getElementById(`slider-${this.dataset.section}`);
    const thumbnailsGridySlider = document.getElementById(
      `thumbnail-slider-${this.dataset.section}`
    );

    if (mediaGridySlider) {
      mediaGridySlider.scrollToSlideByID(`item-${this.currentVariant.featured_media.id}`);
    }

    if (thumbnailsGridySlider) {
      thumbnailsGridySlider.scrollToSlideByID(
        `thumbnail-item-${this.currentVariant.featured_media.id}`
      );
    }

    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    const isGrid = mediaGridySlider.dataset.desktopEnabled === 'false' ? true : false;
    if (isDesktop && isGrid) {
      document.getElementById(`item-${this.currentVariant.featured_media.id}`)?.scrollIntoView();
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}-product-buy-bar`
    );
    productForms.forEach(productForm => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      variant => this.querySelector(':checked').value === variant.option1
    );
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter(
          variant => variant.available && variant[`option${index}`] === previousOptionSelected
        )
        .map(variantOption => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value');
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace(
          '[value]',
          input.getAttribute('value')
        );
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  updateInventoryStatus() {
    this.productInventoryNode = document.querySelector('[data-product-inventory]');

    if (
      !this.productInventoryNode ||
      !window.inventories ||
      !window.inventories[this.dataset.productId]
    )
      return;

    this.inventoryThreshold = parseInt(this.productInventoryNode.dataset.threshold);
    this.showQuantityInStock = this.productInventoryNode.dataset.showQuantityInStock;

    const variantInventoryObject =
      window.inventories[this.dataset.productId][this.currentVariant?.id];

    if (!this.currentVariant || !this.currentVariant.inventory_management) {
      this.toggleInventoryQuantity(false);
      this.toggleIncomingInventory(false);

      return;
    }

    if (this.currentVariant.inventory_management === 'shopify') {
      if (
        variantInventoryObject.quantity <= 0 &&
        variantInventoryObject.incoming === 'true' &&
        variantInventoryObject.quantity < this.inventoryThreshold
      ) {
        this.toggleInventoryQuantity(false);
        this.toggleIncomingInventory(true, variantInventoryObject.next_incoming_date);
      } else {
        this.toggleInventoryQuantity(true, variantInventoryObject.quantity);
        this.toggleIncomingInventory(false);
      }
    }
  }

  toggleInventoryQuantity(show, quantity) {
    if (show === false) {
      this.productInventoryNode.classList.add('hide');
      return;
    }

    this.productInventoryNode.classList.remove(
      'inventory-status--in-stock',
      'inventory-status--low',
      'inventory-status--sold-out',
      'inventory-status--sold-out-continue-selling'
    );

    if (
      this.currentVariant &&
      this.currentVariant.available &&
      parseInt(quantity) <= parseInt(this.inventoryThreshold) &&
      parseInt(quantity) > 0
    ) {
      this.productInventoryNode.classList.add('inventory-status--low');
      if (this.showQuantityInStock === 'true') {
        this.productInventoryNode.firstElementChild.textContent =
          window.variantStrings.inventory.few_left_with_quantity.replace('[quantity]', quantity);
      } else {
        this.productInventoryNode.firstElementChild.textContent =
          window.variantStrings.inventory.few_left;
      }
    } else if (
      this.currentVariant &&
      this.currentVariant.available &&
      parseInt(quantity) > parseInt(this.inventoryThreshold)
    ) {
      this.productInventoryNode.classList.add('inventory-status--in-stock');
      if (this.showQuantityInStock === 'true') {
        this.productInventoryNode.firstElementChild.textContent =
          window.variantStrings.inventory.in_stock_with_quantity.replace('[quantity]', quantity);
      } else {
        this.productInventoryNode.firstElementChild.textContent =
          window.variantStrings.inventory.in_stock;
      }
    } else if (this.currentVariant && this.currentVariant.available && parseInt(quantity) < 1) {
      this.productInventoryNode.classList.add('inventory-status--sold-out-continue-selling');
      this.productInventoryNode.firstElementChild.textContent =
        window.variantStrings.inventory.out_of_stock_continue_selling;
    } else {
      this.productInventoryNode.classList.add('inventory-status--sold-out');
      this.productInventoryNode.firstElementChild.textContent =
        window.variantStrings.inventory.out_of_stock;
    }

    this.productInventoryNode.classList.remove('hide');
  }

  toggleIncomingInventory(show, date) {
    const incomingInventoryNode = document.querySelector('[data-incoming-inventory]');
    if (!incomingInventoryNode) return;

    if (show == false && incomingInventoryNode) {
      incomingInventoryNode.classList.add('hide');

      return;
    }

    let string = !date
      ? window.variantStrings.inventory.waiting_for_stock
      : window.variantStrings.inventory.will_be_in_stock_after.replace('[date]', date);

    incomingInventoryNode.classList.remove('hide');
    incomingInventoryNode.firstElementChild.textContent = string;
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  // Static cache for variant info fetches
  static variantInfoCache = new Map();
  static lastRequestVariantId = null;

  renderProductInfo() {
    // Store the current variant ID to track which request is the latest
    const currentVariantId = this.currentVariant.id;
    VariantSelects.lastRequestVariantId = currentVariantId;
    // Update global tracker - important to ensure synchronization
    window.latestVariantId = currentVariantId;

    const cacheKey = `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`;
    const cache = VariantSelects.variantInfoCache;
    if (cache.has(cacheKey)) {
      const responseText = cache.get(cacheKey);
      this._renderProductInfoFromResponse(responseText, currentVariantId);
      return;
    }
    fetch(cacheKey)
      .then(response => response.text())
      .then(responseText => {
        cache.set(cacheKey, responseText);
        // Only render the response if this is still the most recent request
        if (VariantSelects.lastRequestVariantId === currentVariantId) {
          this._renderProductInfoFromResponse(responseText, currentVariantId);
        }
      });
  }

  // Helper to render product info from responseText
  _renderProductInfoFromResponse(responseText, variantId) {
    // Skip rendering if this response is for an outdated variant request
    if (variantId !== VariantSelects.lastRequestVariantId) {
      return;
    }

    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const sourceSection = html.querySelector('.shopify-section');
    const destinationSection = this.closest('.shopify-section');

    if (sourceSection && destinationSection) {
        const sourcePriceContainers = sourceSection.querySelectorAll('.product-price-container');
        const destinationPriceContainers = destinationSection.querySelectorAll('.product-price-container');

        destinationPriceContainers.forEach((container, index) => {
            if (sourcePriceContainers[index]) {
                container.innerHTML = sourcePriceContainers[index].innerHTML;
            }
        });
    }

    const skuSource = html.getElementById(
      `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
    );
    const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
    if (skuSource && skuDestination) {
      skuDestination.innerHTML = skuSource.innerHTML;
      skuDestination.classList.toggle('hidden', skuSource.classList.contains('hidden'));
    }

    const variantData = JSON.parse(html.querySelector('[data-selected-variant]').innerHTML);

    // Instead of calling updatePrices directly, dispatch an event
    // This centralizes price updates in one place
    document.dispatchEvent(
      new CustomEvent('variant:changed', {
        detail: {
          price: variantData.price,
          comparePrice: variantData.compare_at_price,
          variantId: variantId, // Include the variant ID in the event data
          variantTitle: variantData.title, // Include the variant title for frame updates
        },
      })
    );

    this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    const multiplyButtons = productForm.querySelectorAll('.btn--add-multiplier');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
      if (multiplyButtons) {
        multiplyButtons.forEach(button => {
          button.disabled = true;
        });
      }
    } else {
      addButton.removeAttribute('disabled');
      if (this.dataset.isPreorder == 'true') {
        addButtonText.textContent = window.variantStrings.preOrder;
      } else {
        addButtonText.textContent = window.variantStrings.addToCart;
      }
      if (multiplyButtons) {
        multiplyButtons.forEach(button => {
          button.removeAttribute('disabled');
        });
      }
    }

    const productForm_BuyBar = document.getElementById(
      `product-form-${this.dataset.section}-product-buy-bar`
    );
    if (productForm_BuyBar) {
      const addButton_BuyBar = productForm_BuyBar.querySelector('[name="add"]');
      const addButtonText_BuyBar = productForm_BuyBar.querySelector('[name="add"] > span');

      if (disable) {
        addButton_BuyBar.setAttribute('disabled', 'disabled');
        if (text) addButtonText_BuyBar.textContent = text;
      } else {
        addButton_BuyBar.removeAttribute('disabled');
        if (this.dataset.isPreorder == 'true') {
          addButtonText_BuyBar.textContent = window.variantStrings.preOrder;
        } else {
          addButtonText_BuyBar.textContent = window.variantStrings.addToCart;
        }
      }
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('hidden');

    const button_BuyBar = document.getElementById(
      `product-form-${this.dataset.section}-product-buy-bar`
    );
    const addButton_BuyBar = button_BuyBar.querySelector('[name="add"]');
    const addButtonText_BuyBar = button_BuyBar.querySelector('[name="add"] > span');
    const price_BuyBar = document.getElementById(`price-${this.dataset.section}-product-buy-bar`);
    if (!addButton_BuyBar) return;
    addButtonText_BuyBar.textContent = window.variantStrings.unavailable;
    if (price_BuyBar) price_BuyBar.classList.add('hidden');
  }

  getVariantData() {
    this.variantData =
      this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map(fieldset => {
      return Array.from(fieldset.querySelectorAll('input')).find(radio => radio.checked).value;
    });

    this.updateLegends();
  }

  updateLegends() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    fieldsets.forEach(fieldset => {
      const legend = fieldset.querySelector('legend [data-variant-legend]');
      const selectedOption = Array.from(fieldset.querySelectorAll('input')).find(
        radio => radio.checked
      );
      if (legend) {
        legend.textContent = selectedOption.getAttribute('value');
      }
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class ProductBuyBar extends HTMLElement {
  constructor() {
    'use strict';
    super();
    const SHOW = 'show';
    // Hide the sticky bar whenever the real size picker or add-to-cart button (or the footer)
    // is on screen; otherwise two buy controls compete and the page feels cluttered.
    const visible = new Set();
    const update = () => {
      if (visible.size === 0 && window.scrollY > 200) this.classList.add(SHOW);
      else this.classList.remove(SHOW);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
        update();
      },
      { threshold: 0 }
    );
    const watch = () => {
      const main = document.querySelector('[id^="MainProduct-"], .main-product, .product-template') || document;
      const targets = [
        ...main.querySelectorAll('.product-variant-picker, variant-selects, variant-radios, .variant-picker, .frame-selector'),
        ...[...main.querySelectorAll('.product-form__submit, #button-check-option')].filter((x) => !x.closest('product-buy-bar')),
        document.querySelector('footer.footer'),
      ].filter(Boolean);
      if (!targets.length) {
        const f = document.querySelector('.product-form');
        if (f) targets.push(f);
      }
      targets.forEach((t) => observer.observe(t));
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
    else watch();
    window.addEventListener('scroll', update, { passive: true });
  }
}
customElements.define('product-buy-bar', ProductBuyBar);

class UpsellCard extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.image = this.querySelector('.card-media img');

    if (this.select) {
      this.select.addEventListener('change', event => {
        const selectedOption = event.target.options[event.target.selectedIndex];
        const newImgSrc = selectedOption.getAttribute('data-variant-img-src');
        if (newImgSrc) {
          this.updateImage(newImgSrc);
        }
      });
    }
  }

  updateImage(newImgSrc) {
    if (this.image) {
      this.image.src = newImgSrc;
    }
  }
}

customElements.define('upsell-card', UpsellCard);
